import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createCheckoutSession, STRIPE_PRICE_IDS } from '@/lib/stripe';
import { useAuth } from '@/hooks/useAuth';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PricingPlan {
  id: 'monthly' | 'yearly';
  name: string;
  price: number;
  billingPeriod: string;
  description: string;
  features: string[];
  popular?: boolean;
  stripePriceId: string;
  saveText?: string;
}

const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 2.99,
    billingPeriod: 'month',
    description: 'Perfect for trying out Pravos',
    stripePriceId: STRIPE_PRICE_IDS.MONTHLY,
    features: [
      'Unlimited deep work sessions',
      'Advanced timer insights',
      'Complete task management',
      'Focus music library',
      'Productivity analytics',
      'Streak tracking',
    ],
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: 28,
    billingPeriod: 'year',
    description: 'Best value - Save 22%',
    stripePriceId: STRIPE_PRICE_IDS.YEARLY,
    saveText: 'Save $8/year',
    popular: true,
    features: [
      'All monthly features',
      'Save 22% annually',
      'Priority support',
      'Early access to new features',
      'Annual productivity report',
    ],
  },
];

export function UpgradeModal({ isOpen, onClose }: UpgradeModalProps) {
  const { isAuthenticated } = useAuth();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handlePlanSelect = async (plan: PricingPlan) => {
    if (!isAuthenticated) {
      setErrorMessage('Please sign in to continue');
      return;
    }

    try {
      setLoadingPlanId(plan.id);
      setErrorMessage(null);

      // Create checkout session
      const { url } = await createCheckoutSession({
        priceId: plan.stripePriceId,
        planId: plan.id,
      });

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      const message = error instanceof Error ? error.message : 'Failed to start checkout';
      setErrorMessage(message);
      setLoadingPlanId(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="relative w-full max-w-5xl bg-glass-dark border border-glass-border rounded-[20px] shadow-glass-inset overflow-hidden pointer-events-auto"
            >
              {/* Header */}
              <div className="relative px-8 pt-8 pb-6 border-b border-glass-border">
                <button
                  onClick={onClose}
                  className="absolute top-6 right-6 text-text-secondary hover:text-text-primary transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-[10px] bg-brand-purple/10 border border-brand-purple/20">
                    <Zap className="h-5 w-5 text-brand-purple" />
                  </div>
                  <h2 className="text-[28px] font-semibold text-text-primary">
                    Upgrade to Pro
                  </h2>
                </div>
                <p className="text-body text-text-secondary">
                  Unlock unlimited deep work sessions and advanced analytics
                </p>
              </div>

              {/* Pricing Plans */}
              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {PRICING_PLANS.map((plan) => (
                    <motion.div
                      key={plan.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: plan.id === 'monthly' ? 0.1 : 0.2 }}
                      className={`relative bg-glass-dark border rounded-[15px] p-6 ${
                        plan.popular
                          ? 'border-brand-purple shadow-glow-sm'
                          : 'border-glass-border'
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-button-gradient rounded-full text-xs font-medium text-white">
                          Most Popular
                        </div>
                      )}

                      <div className="mb-4">
                        <h3 className="text-[22px] font-semibold text-text-primary mb-1">
                          {plan.name}
                        </h3>
                        <p className="text-sm text-text-secondary">
                          {plan.description}
                        </p>
                      </div>

                      <div className="mb-6">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[40px] font-bold text-text-primary">
                            ${plan.price}
                          </span>
                          <span className="text-text-secondary">
                            /{plan.billingPeriod}
                          </span>
                        </div>
                        {plan.saveText && (
                          <p className="text-sm text-success-green mt-1">
                            {plan.saveText}
                          </p>
                        )}
                      </div>

                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-brand-purple flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-text-primary">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <Button
                        variant={plan.popular ? 'pravos-primary' : 'pravos-secondary'}
                        className="w-full"
                        onClick={() => handlePlanSelect(plan)}
                        disabled={loadingPlanId === plan.id}
                      >
                        {loadingPlanId === plan.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          'Get Started'
                        )}
                      </Button>
                    </motion.div>
                  ))}
                </div>

                {/* Error Message */}
                {errorMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-error-pink/10 border border-error-pink/20 rounded-[12px] text-center"
                  >
                    <p className="text-sm text-error-pink">{errorMessage}</p>
                  </motion.div>
                )}

                {/* Footer Note */}
                <div className="pt-6 border-t border-glass-border text-center">
                  <p className="text-xs text-text-secondary">
                    All plans include a 30-day money-back guarantee. Cancel anytime.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
